/**
 * The conversation-owned harness (MT-3910): a conversation belongs to the maker, not to a version,
 * and carries its own write target, its plans, its read grants and a named ending.
 *
 * Traces to the harness's `docs/harness/29-conversation-owned-plans.md` §7 and its
 * `service/conversation_models.py`. Wire names are kept as the harness spells them, the way
 * `harness.types.ts` keeps them, so a field can be grepped across both repositories.
 *
 * Every guard is `toX(value: unknown): X | null`, as there: the ids a caller acts on are required,
 * and everything else defaults to its empty form, so an additive wire change is not an outage.
 */
import {
  asArray,
  asBoolean,
  asNumber,
  asString,
  asStringList,
  isRecord,
} from '@flowx/designer/modules/ai-builder/utils/payload.util'

/**
 * Where a conversation's changes land (D2), fixed at its first confirmed change. `head_version_id`
 * advances with every commit the harness makes; the first run that cuts an execution branch moves
 * `branch_*` to it. The workspace and the app never move in this iteration: there is no retarget.
 */
export type HarnessWriteTarget = {
  workspace_id: string
  app_id: string
  branch_name: string
  head_version_id: string
  /** The commit the plan was approved against. Null for a brand-new project with no commit yet. */
  anchor_commit_id: string | null
  /** Null until the branch is known by id — a plan confirmed but never run. */
  branch_id: string | null
  base_branch_id: string | null
  base_branch_name: string | null
}

/** Where the maker was when they last spoke (D3). Advisory: reads default to it, writes never do. */
export type HarnessTurnContext = {
  workspace_id: string
  app_id: string
  app_version_id: string
  branch: string
}

/** How a plan version came to be approved (D6). `request` is a change the maker's own words
 * confirmed; `envelope` one the agent made inside what the maker allowed; `legacy` a plan approved
 * before the harness recorded approvers at all. */
export type HarnessApprovalType = 'explicit' | 'request' | 'envelope' | 'legacy'

export type HarnessApproval = {
  subject_id: string
  username: string
  display_name: string
  at: string
  type: HarnessApprovalType
}

export type HarnessPlanSummaryState =
  'DRAFT' | 'READY' | 'EXECUTING' | 'PAUSED' | 'COMPLETED' | 'ABANDONED'

/** One of the conversation's plans, the active one or a finished one (D9). */
export type HarnessPlanSummary = {
  plan_id: string
  state: HarnessPlanSummaryState
  version: number
  title: string
  created_at: string
  confirmed_at: string
  /** The current version's approval, when it has one. */
  approval: HarnessApproval | null
}

/** Where the conversation stands, derived by the harness on every read. */
export type HarnessConversationPhase =
  | 'exploring'
  | 'preparing'
  | 'ready_to_run'
  | 'running'
  | 'waiting_on_human'
  | 'blocked'
  | 'settled'

/** How a settled conversation ended. There is no `deleted`: a deleted conversation is gone. */
export type HarnessConversationEnding =
  'answered' | 'awaiting_merge' | 'merged' | 'discarded' | 'abandoned' | 'blocked'

/** What became of the latest plan: abandoned, or what App Manager says of its branch. */
export type HarnessPlanDisposition =
  'awaiting_merge' | 'merged' | 'discarded' | 'abandoned' | 'unknown'

export type HarnessBranchDisposition = {
  status: 'awaiting_merge' | 'merged' | 'discarded' | 'unknown'
  branch_id: string
  branch_name: string
  /** Why it was discarded, when the branch was archived with a reason. */
  reason: string
}

/** The derived fields every conversation read carries. */
export type HarnessConversationStanding = {
  /** `settled` once the conversation has an ending; the list's `state` filter reads this. */
  state: 'active' | 'settled'
  phase: HarnessConversationPhase
  ending: HarnessConversationEnding | null
  disposition: HarnessPlanDisposition | null
  branch_disposition: HarnessBranchDisposition | null
}

/** One row of `GET /conversations`. */
export type HarnessConversationSummary = HarnessConversationStanding & {
  session_id: string
  title: string
  /** The session's own turn status (`idle`, `running`, `waiting`, …). */
  status: string
  updated_at: string
  write_target: HarnessWriteTarget | null
  last_context: HarnessTurnContext | null
  /** The active plan, if any. */
  plan: HarnessPlanSummary | null
  plan_count: number
  /** Set on a continuation session from before conversations were owned. */
  continued_from: string | null
}

export type HarnessConversationPage = {
  conversations: readonly HarnessConversationSummary[]
  next_cursor: string | null
}

/** `filter=project` needs `app_id`; `filter=plan` is the conversations with an active plan. */
export type HarnessConversationFilter = 'all' | 'project' | 'plan'

export type HarnessConversationQuery = {
  filter: HarnessConversationFilter
  app_id?: string
  state?: 'active' | 'settled'
  q?: string
  cursor?: string
  limit?: number
}

/** A read grant (D11): read-only, this conversation only, expiring when idle. `app_id` null is the
 * whole workspace. */
export type HarnessGrant = {
  grant_id: string
  workspace_id: string
  app_id: string | null
  created_at: string
  expires_at: string
  revoked_at: string | null
}

/** The conversation half of `GET /sessions/{id}` read as its owner — what the record alone, and so
 * a `lifecycle` frame, does not carry. */
export type HarnessConversationDetails = HarnessConversationStanding & {
  plans: readonly HarnessPlanSummary[]
  grants: readonly HarnessGrant[]
  continued_from: string | null
}

/** Work that outlives a deleted conversation: commits and their trailers stay on the branch. */
export type HarnessCommittedWork = {
  plan_id: string
  branch_name: string
  commit_ids: readonly string[]
}

/** `GET /sessions/{id}/deletion-preview` (D10). */
export type HarnessDeletionPreview = {
  deletable: boolean
  refused_reason: 'turn_running' | 'execution_running' | null
  /** What cannot be deleted from here: commits and their trailers, audit, Observatory. */
  retained: readonly string[]
  committed_work: readonly HarnessCommittedWork[]
  /** Specialist jobs a delete cancels first. */
  background_jobs: number
}

/** SSE `target_advanced`: the harness's own commit moved the write target's head (D8). */
export type HarnessTargetAdvanced = {
  session_id: string
  previous_head_version_id: string | null
  write_target: HarnessWriteTarget
}

/** The adjustment ladder (D15). L0 repairs are silent; L1 is made and shown; L2 is a diff for the
 * maker to approve; L3 asks for the description again; L4 explains why nothing can go on. */
export type HarnessAdjustmentLevel = 'L0' | 'L1' | 'L2' | 'L3' | 'L4'

/** SSE `plan_adjusted` — streamed during a run and kept among the session's events. */
export type HarnessPlanAdjustment = {
  plan_id: string
  level: HarnessAdjustmentLevel
  from_version: number
  /** For L2 and L3 the version the maker's confirmation would make; null when blocked (L4). */
  to_version: number | null
  reason: string
  /** Work item ids, as the harness diffs the unexecuted suffix. */
  removed: readonly string[]
  added: readonly string[]
  changed: readonly string[]
}

/** The `read_grant` interrupt's payload: the project the maker's page is in, which this
 * conversation cannot read without their say-so. Named by the harness, never by the model. */
export type HarnessReadGrantRequest = {
  workspace_id: string
  app_id: string
  whole_workspace: boolean
  reason: string
}

const PLAN_SUMMARY_STATES: readonly HarnessPlanSummaryState[] = [
  'DRAFT',
  'READY',
  'EXECUTING',
  'PAUSED',
  'COMPLETED',
  'ABANDONED',
]

const APPROVAL_TYPES: readonly HarnessApprovalType[] = ['explicit', 'request', 'envelope', 'legacy']

const PHASES: readonly HarnessConversationPhase[] = [
  'exploring',
  'preparing',
  'ready_to_run',
  'running',
  'waiting_on_human',
  'blocked',
  'settled',
]

const ENDINGS: readonly HarnessConversationEnding[] = [
  'answered',
  'awaiting_merge',
  'merged',
  'discarded',
  'abandoned',
  'blocked',
]

const DISPOSITIONS: readonly HarnessPlanDisposition[] = [
  'awaiting_merge',
  'merged',
  'discarded',
  'abandoned',
  'unknown',
]

const BRANCH_STATUSES: readonly HarnessBranchDisposition['status'][] = [
  'awaiting_merge',
  'merged',
  'discarded',
  'unknown',
]

const ADJUSTMENT_LEVELS: readonly HarnessAdjustmentLevel[] = ['L0', 'L1', 'L2', 'L3', 'L4']

/** A target that names no project cannot be written to, and one with no head cannot be read at,
 * so those three are required; the rest the harness resolves lazily. */
export function toWriteTarget(value: unknown): HarnessWriteTarget | null {
  if (!isRecord(value)) {
    return null
  }

  const workspaceId = asString(value['workspace_id'])
  const appId = asString(value['app_id'])
  const head = asString(value['head_version_id'])

  if (!workspaceId || !appId || !head) {
    return null
  }

  return {
    workspace_id: workspaceId,
    app_id: appId,
    branch_name: asString(value['branch_name']),
    head_version_id: head,
    anchor_commit_id: asString(value['anchor_commit_id']) || null,
    branch_id: asString(value['branch_id']) || null,
    base_branch_id: asString(value['base_branch_id']) || null,
    base_branch_name: asString(value['base_branch_name']) || null,
  }
}

export function toTurnContext(value: unknown): HarnessTurnContext | null {
  if (!isRecord(value)) {
    return null
  }

  const appVersionId = asString(value['app_version_id'])

  return appVersionId
    ? {
        workspace_id: asString(value['workspace_id']),
        app_id: asString(value['app_id']),
        app_version_id: appVersionId,
        branch: asString(value['branch']) || 'main',
      }
    : null
}

export function toApproval(value: unknown): HarnessApproval | null {
  if (!isRecord(value) || !APPROVAL_TYPES.includes(value['type'] as HarnessApprovalType)) {
    return null
  }

  return {
    subject_id: asString(value['subject_id']),
    username: asString(value['username']),
    display_name: asString(value['display_name']),
    at: asString(value['at']),
    type: value['type'] as HarnessApprovalType,
  }
}

export function toPlanSummary(value: unknown): HarnessPlanSummary | null {
  if (!isRecord(value)) {
    return null
  }

  const planId = asString(value['plan_id'])
  const state = value['state']

  if (!planId || !PLAN_SUMMARY_STATES.includes(state as HarnessPlanSummaryState)) {
    return null
  }

  return {
    plan_id: planId,
    state: state as HarnessPlanSummaryState,
    version: asNumber(value['version'], 0),
    title: asString(value['title']),
    created_at: asString(value['created_at']),
    confirmed_at: asString(value['confirmed_at']),
    approval: toApproval(value['approval']),
  }
}

export function toGrant(value: unknown): HarnessGrant | null {
  if (!isRecord(value)) {
    return null
  }

  const grantId = asString(value['grant_id'])
  const workspaceId = asString(value['workspace_id'])

  return grantId && workspaceId
    ? {
        grant_id: grantId,
        workspace_id: workspaceId,
        app_id: asString(value['app_id']) || null,
        created_at: asString(value['created_at']),
        expires_at: asString(value['expires_at']),
        revoked_at: asString(value['revoked_at']) || null,
      }
    : null
}

function toBranchDisposition(value: unknown): HarnessBranchDisposition | null {
  if (
    !isRecord(value) ||
    !BRANCH_STATUSES.includes(value['status'] as HarnessBranchDisposition['status'])
  ) {
    return null
  }

  return {
    status: value['status'] as HarnessBranchDisposition['status'],
    branch_id: asString(value['branch_id']),
    branch_name: asString(value['branch_name']),
    reason: asString(value['reason']),
  }
}

/** An unknown phase reads as exploring rather than failing the read: it names where the
 * conversation is, and a newer harness adding one must not cost the maker the conversation. */
function toStanding(value: Record<string, unknown>): HarnessConversationStanding {
  const phase = PHASES.includes(value['phase'] as HarnessConversationPhase)
    ? (value['phase'] as HarnessConversationPhase)
    : 'exploring'

  return {
    state: value['state'] === 'settled' ? 'settled' : 'active',
    phase,
    ending: ENDINGS.includes(value['ending'] as HarnessConversationEnding)
      ? (value['ending'] as HarnessConversationEnding)
      : null,
    disposition: DISPOSITIONS.includes(value['disposition'] as HarnessPlanDisposition)
      ? (value['disposition'] as HarnessPlanDisposition)
      : null,
    branch_disposition: toBranchDisposition(value['branch_disposition']),
  }
}

/** The conversation fields of an owner read, or null when the body has none of them — a read
 * scoped to a version answers the plain record. `owner` is the tell: every owner read carries it,
 * and nothing else does. */
export function toConversationDetails(value: unknown): HarnessConversationDetails | null {
  if (!isRecord(value) || !isRecord(value['owner'])) {
    return null
  }

  return {
    ...toStanding(value),
    plans: asArray(value['plans']).flatMap((plan) => toPlanSummary(plan) ?? []),
    grants: asArray(value['grants']).flatMap((grant) => toGrant(grant) ?? []),
    continued_from: asString(value['continued_from']) || null,
  }
}

export function toConversationSummary(value: unknown): HarnessConversationSummary | null {
  if (!isRecord(value)) {
    return null
  }

  const sessionId = asString(value['session_id'])

  return sessionId
    ? {
        ...toStanding(value),
        session_id: sessionId,
        title: asString(value['title']),
        status: asString(value['status']),
        updated_at: asString(value['updated_at']),
        write_target: toWriteTarget(value['write_target']),
        last_context: toTurnContext(value['last_context']),
        plan: toPlanSummary(value['plan']),
        plan_count: asNumber(value['plan_count'], 0),
        continued_from: asString(value['continued_from']) || null,
      }
    : null
}

/** A row this client cannot read is dropped rather than failing the page: the others are still
 * the maker's, and still openable. */
export function toConversationPage(value: unknown): HarnessConversationPage | null {
  if (!isRecord(value) || !Array.isArray(value['conversations'])) {
    return null
  }

  return {
    conversations: value['conversations'].flatMap((row) => toConversationSummary(row) ?? []),
    next_cursor: asString(value['next_cursor']) || null,
  }
}

export function toDeletionPreview(value: unknown): HarnessDeletionPreview | null {
  if (!isRecord(value) || typeof value['deletable'] !== 'boolean') {
    return null
  }

  const refused = value['refused_reason']

  return {
    deletable: value['deletable'],
    refused_reason: refused === 'turn_running' || refused === 'execution_running' ? refused : null,
    retained: asStringList(value['retained']),
    committed_work: asArray(value['committed_work']).flatMap((work) =>
      isRecord(work) && asString(work['plan_id'])
        ? [
            {
              plan_id: asString(work['plan_id']),
              branch_name: asString(work['branch_name']),
              commit_ids: asStringList(work['commit_ids']),
            },
          ]
        : []
    ),
    background_jobs: asNumber(value['background_jobs'], 0),
  }
}

export function toTargetAdvanced(value: unknown): HarnessTargetAdvanced | null {
  if (!isRecord(value)) {
    return null
  }

  const target = toWriteTarget(value['write_target'])
  const sessionId = asString(value['session_id'])

  return target && sessionId
    ? {
        session_id: sessionId,
        previous_head_version_id: asString(value['previous_head_version_id']) || null,
        write_target: target,
      }
    : null
}

export function toPlanAdjustment(value: unknown): HarnessPlanAdjustment | null {
  if (!isRecord(value) || !ADJUSTMENT_LEVELS.includes(value['level'] as HarnessAdjustmentLevel)) {
    return null
  }

  const diff = isRecord(value['diff']) ? value['diff'] : {}
  const toVersion = value['to_version']

  return {
    plan_id: asString(value['plan_id']),
    level: value['level'] as HarnessAdjustmentLevel,
    from_version: asNumber(value['from_version'], 0),
    to_version: typeof toVersion === 'number' ? toVersion : null,
    reason: asString(value['reason']),
    removed: asStringList(diff['removed']),
    added: asStringList(diff['added']),
    changed: asStringList(diff['changed']),
  }
}

export function toReadGrantRequest(value: unknown): HarnessReadGrantRequest {
  const payload = isRecord(value) ? value : {}

  return {
    workspace_id: asString(payload['workspace_id']),
    app_id: asString(payload['app_id']),
    whole_workspace: asBoolean(payload['whole_workspace'], false),
    reason: asString(payload['reason']),
  }
}
